import { useCallback, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { User } from "../../../mp-database"
import {
  getOriginalUserData,
  removeImpersonatedUserData,
  setExitingImpersonationFlag,
  setOriginalUserData
} from "../helpers/impersonationStorageManage"
import { logOutAction } from "../../redux/modules/users"
import { RootState } from "../../redux/store"
import { enqueueSnackbar } from "notistack"
import { getRequest } from "../../../mp-utils/generic-utils"
import { getEnv } from "../../../../utils/env"

// Hook for creation leave impersonation method
export const useLeaveImpersonationManage = () => {
  const [leaveImpersonationLoading, setLeaveImpersonationLoading] =
    useState<boolean>(false)

  const dispatch = useDispatch() as any

  const { user } = useSelector((state: RootState) => ({
    user: state.users.testUser as User
  }))

  const [originalUserStorageData, impersonationEnable] = useMemo(() => {
    const originalUserStorageData = getOriginalUserData()

    return [originalUserStorageData, !!originalUserStorageData?.userId]
  }, [user?.id])

  // Method for leaving impersonation and go to the original user's account
  // (we log out and then go to the `useExitingImpersonationManage` hook for login).
  const leaveImpersonationHandler = useCallback(async () => {
    let originalUser = null
    let originalUserCustomToken = null

    try {
      setLeaveImpersonationLoading(true)

      const originalUserData = await getRequest(
        `${getEnv("CLOUD_FUNCTION_ROOT")}/authGroup-unimpersonate`
      )

      const { customToken, user } = JSON.parse(originalUserData || "{}")

      if (!customToken || !user) {
        throw new Error("Custom Token or original user is not defined")
      }

      originalUser = user
      originalUserCustomToken = customToken
    } catch (error) {
      enqueueSnackbar(
        "An error occurred while trying to leave impersonation. Please try again. If the error persists, contact support for help",
        {
          variant: "error",
          autoHideDuration: 10000
        }
      )
    } finally {
      setLeaveImpersonationLoading(false)

      dispatch(
        await logOutAction(() => {
          // After logout we should remove impersonated user's data from storage
          // and save flag for exiting and original user's data
          removeImpersonatedUserData()
          setExitingImpersonationFlag(true)

          if (!!originalUser && !!originalUserCustomToken) {
            setOriginalUserData({
              userId: originalUser?.id,
              userRole: originalUser?.role,
              customToken: originalUserCustomToken,
              authUrl: `/auth/token?token=${originalUserCustomToken}&user=${originalUser?.id}&role=${originalUser?.role}`,
              backUrl: originalUserStorageData?.backUrl
            })
          }
        })
      )
    }
  }, [originalUserStorageData])

  return {
    impersonationEnable,
    leaveImpersonationLoading,
    leaveImpersonationHandler
  }
}
